/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { UserProfile } from "@microsoft/shifts-models-data";
import { UserInfo } from "@microsoft/shifts-models-view";
// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { DataService } from "sh-services/dataservice/DataService";
import userProfilesUpdated from "sh-stores/sh-userprofiles-store/actions/setUserProfiles";

import { IUserProfileDataService } from "./IUserProfileDataService";
import { UserProfileDataProvider } from "./UserProfileDataProvider";
import { UserProfileMapper } from "./UserProfileMapper";

/**
 * The data service for user profile.
 */
export class UserProfileDataService extends DataService implements IUserProfileDataService {
    /**
     * Instantiates a new UserProfileDataService.
     * @param requestedUsersIds The users ids who were already requested to prevent from fetching their info multiple times.
     */
    constructor(private requestedUsersIds: Set<string> = new Set()) {
        super();
    }

    /**
     * TODO: Review 'setDataInState' arg to either move it to the constructor and/or implement it as part of 'DataService'.
     * Gets the users profiles from given users ids.
     * @param usersIds The users ids.
     * @param setDataInState The callback to set the view models data in state.
     * @returns The users profiles.
     */
    public async getUsersProfiles(usersIds: string[], setDataInState: (users: UserInfo[]) => void): Promise<UserInfo[]> {
        const mapper = new UserProfileMapper();

        const provider = new UserProfileDataProvider({
            database: this.userProfileDatabase,
            setDataInMemory: (usersProfiles: UserProfile[]): void => {
                this.setDataInMemory(usersIds, usersProfiles, setDataInState);
            },
            tenantId: this.tenantId,
            usersIds: usersIds
        });

        const users = await this.getData<UserProfile[]>(provider);
        return mapper.toUsersInfo(users);
    }

    /**
     * Resets the sync state.
     */
    public async resetSyncState(): Promise<void> {
        return Promise.resolve();
    }

    /**
     * Adds missing users information and tag fetched users ids to avoid re-fetching them in the future.
     * @param usersIds The requested users ids.
     * @param users The fetched users info.
     * @returns The fetched and missing users info.
     */
    private addMissingUsersInfo(usersIds: string[], users: UserInfo[]): UserInfo[] {
        const fetchedUsersIds = new Set<string>();

        users.forEach(({ id }) => fetchedUsersIds.add(id));

        usersIds.forEach(userId => {
            // Add empty user info so consumers know whether data has been fetched but not found.
            if (!fetchedUsersIds.has(userId)) {
                users.push({
                    email: "",
                    id: userId,
                    name: ""
                });
            }
        });

        return users;
    }

    /**
     * Converts given data models to view models and set them in state.
     * If a user profile is missing from 'usersProfiles', it adds an empty user info object so consumers can differentiate between missing and not requested user info.
     * @param usersIds The requested users ids.
     * @param usersProfiles The fetched users profiles.
     * @param setDataInState The callback to set the view models in state.
     */
    private setDataInMemory(usersIds: string[], usersProfiles: UserProfile[], setDataInState: (users: UserInfo[]) => void): void {
        const mapper = new UserProfileMapper();
        const usersInfo = mapper.toUsersInfo(usersProfiles);
        // Only adding missing users info at this stage to ensure missing users info are
        // not saved in database.
        const users = this.addMissingUsersInfo(usersIds, usersInfo);

        setDataInState(users);
        userProfilesUpdated(usersProfiles);
    }
}
